import {
  Spread,
  LexicalNode,
  TextNode,
  SerializedTextNode,
  LexicalUpdateJSON,
  $applyNodeReplacement,
  TextFormatType,
} from 'lexical';
import { REFERENCE_CITATION_IS_TOKEN, ReferenceCitationNode } from './ReferenceCitationNode';

export enum ReferenceCitationSeparator {
  COMMA = ',',
  DASH = '-',
  HIDDEN = '',
}

export type SerializedReferenceCitationSeparatorNode = Spread<{
  text: ReferenceCitationSeparator;
  mode: 'token';
}, Omit<SerializedTextNode, 'mode'>>;

export class ReferenceCitationSeparatorNode extends TextNode {
  __text: ReferenceCitationSeparator;

  constructor(separator: ReferenceCitationSeparator, key?: string) {
    super(separator, key);
    this.__mode = REFERENCE_CITATION_IS_TOKEN;
    this.__format = ReferenceCitationNode.fixFormat(this.__format);
  }

  static getType(): string {
    return 'reference-citation-separator';
  }

  static clone(node: ReferenceCitationSeparatorNode): ReferenceCitationSeparatorNode {
    return new ReferenceCitationSeparatorNode(node.__text, node.__key);
  }

  setMode(): this {
    // intentionally prevent change
    return this;
  }

  setTextContent(text: ReferenceCitationSeparator): this {
    return super.setTextContent(text);
  }

  getSeparatorType(): ReferenceCitationSeparator | undefined {
    const txt = this.getTextContent();
    if ((Object.values(ReferenceCitationSeparator) as string[]).includes(txt)) {
      return txt as ReferenceCitationSeparator;
    }

    return undefined;
  }

  convertToComma(): this {
    const type = this.getSeparatorType();
    if (type === ReferenceCitationSeparator.COMMA) {
      return this;
    }

    return this.setTextContent(ReferenceCitationSeparator.COMMA);
  }

  convertToDash(): this {
    const type = this.getSeparatorType();
    if (type === ReferenceCitationSeparator.DASH) {
      return this;
    }

    return this.setTextContent(ReferenceCitationSeparator.DASH);
  }

  convertToHidden(): this {
    const type = this.getSeparatorType();
    if (type === ReferenceCitationSeparator.HIDDEN) {
      return this;
    }

    return this.setTextContent(ReferenceCitationSeparator.HIDDEN);
  }

  setFormat(format: TextFormatType | number): this {
    if (typeof format === 'number') {
      return super.setFormat(ReferenceCitationNode.fixFormat(format));
    }

    return ReferenceCitationNode.isFormatTypeAllowed(format) ? super.setFormat(format) : this;
  }

  toggleFormat(format: TextFormatType): this {
    return ReferenceCitationNode.isFormatTypeAllowed(format) ? super.toggleFormat(format) : this;
  }

  static importJSON(
    serializedNode: SerializedReferenceCitationSeparatorNode,
  ): ReferenceCitationSeparatorNode {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define, no-use-before-define
    return $createReferenceCitationSeparatorNode(serializedNode.text).updateFromJSON(serializedNode);
  }

  updateFromJSON(
    serializedNode: LexicalUpdateJSON<Omit<SerializedTextNode, 'mode' | 'text'>>,
  ): this {
    this.setDetail(serializedNode.detail)
      .setFormat(serializedNode.format)
      .setStyle(serializedNode.style);

    return this;
  }

  exportJSON(): SerializedReferenceCitationSeparatorNode {
    const { mode, ...other } = super.exportJSON();

    return {
      ...other,
      text: this.getSeparatorType() ?? ReferenceCitationSeparator.COMMA,
      mode: 'token',
    };
  }
}

export function $createReferenceCitationSeparatorNode(
  separator: ReferenceCitationSeparator = ReferenceCitationSeparator.COMMA,
): ReferenceCitationSeparatorNode {
  return $applyNodeReplacement(new ReferenceCitationSeparatorNode(separator));
}

export function $isReferenceCitationSeparatorNode(
  node: LexicalNode | null | undefined,
): node is ReferenceCitationSeparatorNode {
  return node instanceof ReferenceCitationSeparatorNode;
}
